// apps/provider/src/hooks/useCredentialingDeny.js
// Does the given provider have a credentialing deny (not_credentialed / in_process)
// for the patient's primary payer group? Frontend mirror of the backend
// resolve_credentialing_deny function: patient insurance → org payer → payer
// group → provider_payer_credentialing. Missing links resolve to deny=false.

import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { usePayerBillingConfig } from './usePayerBillingConfig';

const NEGATIVE_STATUSES = new Set(['not_credentialed', 'in_process']);

export function useCredentialingDeny(patient, providerId) {
  const { orgPayer, isLoading: payerLoading } = usePayerBillingConfig(patient);
  const [record, setRecord] = useState(null);
  const [checking, setChecking] = useState(false);

  const payerGroupId = orgPayer?.payer_group_id || null;

  useEffect(() => {
    let cancelled = false;
    if (!payerGroupId || !providerId) {
      setRecord(null);
      return undefined;
    }
    setChecking(true);
    apiService.getProviderPayerCredentialing({ provider_id: providerId, payer_group_id: payerGroupId })
      .then(records => {
        if (cancelled) return;
        const list = Array.isArray(records) ? records : [];
        setRecord(list.find(r => NEGATIVE_STATUSES.has(r.status)) || null);
      })
      .catch(() => { if (!cancelled) setRecord(null); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [payerGroupId, providerId]);

  return {
    deny: !!record,
    status: record?.status || null,
    payerGroupId,
    payerName: orgPayer?.display_name || null,
    isLoading: payerLoading || checking,
  };
}

/**
 * Provider ids with a negative credentialing status for a payer group —
 * used to exclude them from assigned-signer candidate lists.
 */
export async function fetchDeniedProviderIds(payerGroupId) {
  if (!payerGroupId) return new Set();
  try {
    const [notCred, inProc] = await Promise.all([
      apiService.getProviderPayerCredentialing({ payer_group_id: payerGroupId, status: 'not_credentialed' }),
      apiService.getProviderPayerCredentialing({ payer_group_id: payerGroupId, status: 'in_process' }),
    ]);
    return new Set([...(notCred || []), ...(inProc || [])].map(r => r.provider_id));
  } catch {
    return new Set();
  }
}

/**
 * Map of provider id (as a string) to its negative credentialing status
 * ('not_credentialed' | 'in_process') for a payer group. Returns an empty Map
 * when there is no payer group or the lookup fails.
 */
export async function fetchDeniedProviderRecords(payerGroupId) {
  if (!payerGroupId) return new Map();
  try {
    const [notCred, inProc] = await Promise.all([
      apiService.getProviderPayerCredentialing({ payer_group_id: payerGroupId, status: 'not_credentialed' }),
      apiService.getProviderPayerCredentialing({ payer_group_id: payerGroupId, status: 'in_process' }),
    ]);
    const map = new Map();
    for (const r of [...(notCred || []), ...(inProc || [])]) {
      if (r && r.provider_id != null) map.set(String(r.provider_id), r.status);
    }
    return map;
  } catch {
    return new Map();
  }
}
